import React from "react";
import logoMombasa from "../../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../../core/img/branch-logos/logo_texastrack.png";

// Same 4 branch letterheads and MOMBASA fallback as StockDocumentPreview, so a petty
// cash voucher prints on the same paper as every other document this system produces.
const LOGO_BY_BRANCH = {
  MOMBASA: logoMombasa,
  NAIROBI: logoNairobi,
  SYSTEMS: logoSystems,
  TEXASTRACK: logoTexastrack,
};

const money = (v) =>
  v == null
    ? "-"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const day = (v) =>
  v
    ? new Date(String(v).substring(0, 10) + "T00:00:00").toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const RECEIPT_TEXT = {
  PENDING: "PAID - RECEIPT PENDING",
  REQUIRED: "RECEIPT REQUIRED",
  ATTACHED: "RECEIPT ATTACHED",
};

/**
 * Printable petty cash voucher. Deliberately printable at ANY status - a submitted or
 * approved voucher is exactly what someone carries to get a signature, so gating print
 * on PAID would defeat the point. The status is stamped prominently instead, so an
 * unpaid voucher can never be mistaken for proof of payment.
 *
 * Rendered both on screen (preview) and, via ReactDOMServer.renderToStaticMarkup, into
 * the print popup - one template, not two. Everything is inline-styled for that reason:
 * the popup has no stylesheet.
 */
const VoucherPrintPreview = ({ voucher, copyLabel, signatureImageUrl }) => {
  if (!voucher) return null;

  const logo = LOGO_BY_BRANCH[voucher.branchCode] || logoMombasa;
  const amount = voucher.paidAmount ?? voucher.approvedAmount ?? voucher.requestedAmount;
  const isPaid = voucher.status === "PAID";
  const receiptNote = RECEIPT_TEXT[voucher.receiptStatus];

  const cell = { padding: "6px 8px", verticalAlign: "top", fontSize: "12px" };
  const label = { ...cell, width: "150px", color: "#555", fontWeight: 600 };
  const line = { borderBottom: "1px solid #000", height: "34px" };

  return (
    <div
      style={{
        width: "190mm",
        minHeight: "128mm",
        margin: "0 auto",
        padding: "12mm",
        background: "#fff",
        color: "#000",
        fontFamily: "Arial, Helvetica, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <img src={logo} alt="" style={{ height: "62px", objectFit: "contain" }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "17px", fontWeight: 700, letterSpacing: "1px" }}>PETTY CASH VOUCHER</div>
          <div style={{ fontSize: "13px", marginTop: "2px" }}>
            No. <strong>{voucher.voucherNumber}</strong>
          </div>
          <div style={{ fontSize: "11px", color: "#555" }}>{voucher.branchCode}</div>
          {copyLabel && (
            <div style={{ fontSize: "10px", letterSpacing: "1px", marginTop: "3px", color: "#555" }}>{copyLabel}</div>
          )}
        </div>
      </div>

      {/* The status is stamped, not hidden, precisely because this prints at any status. */}
      <div style={{ marginTop: "10px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
        <span
          style={{
            border: `2px solid ${isPaid ? "#1a7f43" : "#8a6d1f"}`,
            color: isPaid ? "#1a7f43" : "#8a6d1f",
            padding: "2px 10px",
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "1px",
            borderRadius: "3px",
          }}
        >
          {String(voucher.status || "").replace("_", " ")}
        </span>
        {receiptNote && (
          <span
            style={{
              border: "1px solid #b02a2a",
              color: "#b02a2a",
              padding: "2px 8px",
              fontSize: "10.5px",
              fontWeight: 700,
              letterSpacing: "0.5px",
              borderRadius: "3px",
            }}
          >
            {receiptNote}
          </span>
        )}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px", border: "1px solid #000" }}>
        <tbody>
          <tr>
            <td style={label}>Date</td>
            <td style={cell}>{day(voucher.expenseDate)}</td>
            <td style={label}>Requested by</td>
            <td style={cell}>{voucher.requestedByName || "-"}</td>
          </tr>
          <tr>
            <td style={label}>Category</td>
            <td style={cell}>{voucher.categoryName || "-"}</td>
            <td style={label}>Fund</td>
            <td style={cell}>{voucher.fundName || "-"}</td>
          </tr>
          <tr>
            <td style={label}>Paid to</td>
            <td style={cell}>{voucher.paidToEmployeeName || voucher.paidToPersonName || "-"}</td>
            <td style={label}>Supplier</td>
            <td style={cell}>{voucher.supplierName || "-"}</td>
          </tr>
          <tr>
            <td style={label}>Being payment for</td>
            <td style={{ ...cell, minHeight: "40px" }} colSpan={3}>
              {voucher.description || "-"}
            </td>
          </tr>
          <tr>
            <td style={label}>Amount</td>
            <td style={{ ...cell, fontSize: "16px", fontWeight: 700 }} colSpan={3}>
              KES {money(amount)}
            </td>
          </tr>
          {/* Batch168 - only once something has actually been handed back. */}
          {Number(voucher.returnedAmount || 0) > 0 && (
            <tr>
              <td style={label}>Returned</td>
              <td style={cell}>KES {money(voucher.returnedAmount)}</td>
              <td style={label}>Net spend</td>
              <td style={{ ...cell, fontWeight: 700 }}>KES {money(Number(amount || 0) - Number(voucher.returnedAmount))}</td>
            </tr>
          )}
          {voucher.receiptNumber && (
            <tr>
              <td style={label}>Receipt no.</td>
              <td style={cell} colSpan={3}>
                {voucher.receiptNumber}
              </td>
            </tr>
          )}
          {isPaid && (
            <tr>
              <td style={label}>Paid on</td>
              <td style={cell}>{day(voucher.paidDate)}</td>
              <td style={label}>Paid by</td>
              <td style={cell}>{voucher.paidByName || "-"}</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* "Approved by" carries the General Manager's seal (2026-09-09) - the
          company's mark of authority, the same one the quotation prints. The
          other two stay blank ruled lines: Received by and Paid by are signed
          by hand when the cash actually changes hands.

          signatureImageUrl is a data: URI, never a network URL - the print
          popup is an about:blank window with no auth header and no base URL, so
          anything else would render as a broken image there while looking fine
          on screen. Absent seal simply prints the blank line, unchanged.

          Sized like the LPO's (2026-09-21, "bigger to look like real as in lpo"):
          at 38px tall the seal was a thumbnail nobody could read. Same recipe as
          StockDocumentPreview - a fixed landscape box with object-fit: cover, so a
          photographed stamp fills it whatever the upload's shape, and it hangs
          28px below the ruled line the way real ink overlaps a signature line.
          It is absolutely placed inside the 38px slot so the three lines stay
          level; the extra top margin is the room it rises into. */}
      <div style={{ display: "flex", gap: "18px", marginTop: signatureImageUrl ? "78px" : "26px" }}>
        {["Received by", "Paid by", "Approved by"].map((sig) => {
          const isApproval = sig === "Approved by";
          return (
            <div key={sig} style={{ flex: 1 }}>
              <div style={{ height: "38px", position: "relative" }}>
                {isApproval && signatureImageUrl && (
                  <div
                    style={{
                      position: "absolute", left: "50%", bottom: "-28px", transform: "translateX(-50%)",
                      width: "230px", maxWidth: "118%", height: "110px", overflow: "hidden", zIndex: 2,
                    }}
                  >
                    <img
                      src={signatureImageUrl}
                      alt="Approval seal"
                      style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
                    />
                  </div>
                )}
              </div>
              <div style={line} />
              <div style={{ fontSize: "10.5px", color: "#555", marginTop: "3px", position: "relative", zIndex: 3 }}>
                {sig}
                {isApproval && voucher.approvedByName ? ` · ${voucher.approvedByName}` : ""}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: "14px", fontSize: "9.5px", color: "#777", textAlign: "center" }}>
        {voucher.voucherNumber} &middot; printed {new Date().toLocaleString("en-GB")}
      </div>
    </div>
  );
};

export default VoucherPrintPreview;
